import { type WaitListNid } from '#app/__generated__/api.js';
import { AddVerticalFades } from '#app/components/AddVerticalFades/AddVerticalFades.js';
import { Menu } from '#app/components/Menu/Menu.js';
import { animated } from '#app/components/Primitives/Animated.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useContraMutation } from '#app/hooks/useContraMutation.js';
import { useHasUserFeatures } from '#app/hooks/useHasUserFeatures.js';
import { springAnimation } from '#app/utilities/animation.js';
import { CellLayout } from '../CellLayout.js';
import {
  AccessTypeLabel,
  AccessTypeLayout,
  AccessTypePromotion,
  AccessTypeValues,
  Grid,
  LabelAndPromo,
  StyledButton,
  ToggleButton,
  WaitListText,
} from './WaitListMenu.styles.js';
import { ChevronDownIcon } from '@contra/icons/icons';
import { graphql } from '@contra/relay';
import { type ReactNode, useCallback, useState } from 'react';

type WaitListEdge = {
  id: string;
  nid: WaitListNid;
};

const WaitListDescriptionMap: { [key in WaitListNid]?: string } = {
  GROWTH_RECOMMENDATIONS:
    'Waitlist for Growth Band Recommendations Improvements',
  INDEPENDENT_PORTFOLIOS_ALPHA: 'Waitlist for Contra Pro Access',
  OTHER: 'Waitlist used typically for testing purposes',
  PRO_ANALYTICS: 'Waitlist for Contra Pro Analytics',
};

export const WaitListMenu = ({
  children,
  onMutationSuccess,
  userId,
  waitLists,
}: {
  readonly children?: ReactNode;
  readonly onMutationSuccess?: () => void;
  readonly userId: string;
  readonly waitLists: WaitListEdge[];
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const hasPermissions = useHasUserFeatures(['MANAGE_USER_WAITLISTS']);
  const toast = useToast();

  const toggleMenu = useCallback(() => {
    setIsOpen((currentState) => !currentState);
  }, []);

  const [adminRemoveUserFromWaitList] = useContraMutation(graphql`
    mutation WaitListMenuAdminRemoveUserFromWaitListMutation(
      $input: AdminRemoveUserFromWaitListInput!
    ) {
      adminRemoveUserFromWaitList(input: $input) {
        errors {
          __typename
          message
        }
        adminUserSearchResult {
          id
          waitLists {
            edges {
              node {
                id
                nid
              }
            }
          }
        }
      }
    }
  `);

  const onClickRemoveFromList = (waitListNid: WaitListNid) => {
    adminRemoveUserFromWaitList({
      onCompleted: (errors) => {
        const { apiErrors, payloadErrors } = errors;

        if (payloadErrors) {
          toast.error(
            'An unexpected error occurred. Our team has been notified.',
          );

          return;
        }

        if (apiErrors) {
          for (const apiError of apiErrors) {
            toast.error(apiError.message);
          }

          return;
        }

        toast.success('Removed from waitlist!');
        onMutationSuccess?.();
      },
      onError: () => {
        toast.error(
          'Oops! Something went wrong. Our team has been notified. Please try again.',
        );
      },
      variables: {
        input: {
          userId,
          waitListNid,
        },
      },
    });
  };

  return hasPermissions ? (
    <Menu
      isOpen={isOpen}
      toggleMenu={toggleMenu}
    >
      <Menu.Reference>
        <ToggleButton
          as="button"
          onClick={toggleMenu}
          type="button"
        >
          <CellLayout>{children}</CellLayout>
          <animated.div
            animate={{
              rotate: isOpen ? 180 : 0,
            }}
            initial={false}
            transition={springAnimation.fast}
          >
            <ChevronDownIcon size={24} />
          </animated.div>
        </ToggleButton>
      </Menu.Reference>
      <Menu.Panel>
        <AccessTypeLayout>
          <WaitListText>User Waitlists</WaitListText>
          {waitLists.length > 0 ? (
            <AddVerticalFades fadeHeight={124}>
              <Grid>
                {waitLists.map(({ id, nid }) => {
                  return (
                    <div key={`waitlists-${id}`}>
                      <LabelAndPromo>
                        <AccessTypeLabel>{nid}</AccessTypeLabel>
                        <AccessTypePromotion>
                          {WaitListDescriptionMap[nid] ?? null}
                        </AccessTypePromotion>
                      </LabelAndPromo>
                      <AccessTypeValues>
                        <StyledButton
                          onClick={() => onClickRemoveFromList(nid)}
                        >
                          Remove
                        </StyledButton>
                      </AccessTypeValues>
                    </div>
                  );
                })}
              </Grid>
            </AddVerticalFades>
          ) : (
            <AccessTypeLabel>
              – User is not apart of any waitlists –
            </AccessTypeLabel>
          )}
        </AccessTypeLayout>
      </Menu.Panel>
    </Menu>
  ) : null;
};
